import React, { useEffect, useRef } from "react";
import i18n from "../../i18n";

/**
 * Treasury-branded animated dialog — replaces SweetAlert2
 * Usage: import { dialog } from "./TreasuryDialog";
 *   dialog.success("Title", "Message")
 *   dialog.error("Title", "Message")
 *   dialog.confirm("Title", "Message", onConfirm)
 */

const COLORS = {
  green: "#0e3725",
  greenLight: "#1a5c3a",
  gold: "#c9a84c",
  goldDark: "#a07a20",
  red: "#dc2626",
  redLight: "#fef2f2",
  successBg: "#f0fdf4",
  errorBg: "#fef2f2",
};

const styles = `
@import url('https://fonts.googleapis.com/css2?family=Ibarra+Real+Nova:wght@400;600;700&display=swap');

@keyframes trd-backdrop-in {
  from { opacity: 0; }
  to   { opacity: 1; }
}
@keyframes trd-modal-in {
  from { opacity: 0; transform: scale(0.85) translateY(24px); }
  to   { opacity: 1; transform: scale(1) translateY(0); }
}
@keyframes trd-modal-out {
  from { opacity: 1; transform: scale(1) translateY(0); }
  to   { opacity: 0; transform: scale(0.9) translateY(16px); }
}
@keyframes trd-icon-success {
  0%   { stroke-dashoffset: 60; opacity: 0; }
  60%  { opacity: 1; }
  100% { stroke-dashoffset: 0; opacity: 1; }
}
@keyframes trd-icon-error {
  0%   { stroke-dashoffset: 40; opacity: 0; }
  100% { stroke-dashoffset: 0; opacity: 1; }
}
@keyframes trd-ring-pulse {
  0%   { transform: scale(0.7); opacity: 0; }
  50%  { opacity: 1; }
  100% { transform: scale(1.1); opacity: 0; }
}
@keyframes trd-ring-in {
  0%   { transform: scale(0.6); opacity: 0; }
  100% { transform: scale(1); opacity: 1; }
}

.trd-backdrop {
  position: fixed; inset: 0; z-index: 99999;
  background: rgba(10, 28, 18, 0.65);
  backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center;
  animation: trd-backdrop-in 0.2s ease forwards;
}
.trd-backdrop.closing {
  animation: trd-backdrop-in 0.18s ease reverse forwards;
}
.trd-modal {
  background: #fff;
  border-radius: 20px;
  padding: 40px 36px 32px;
  width: 100%; max-width: 400px;
  box-shadow: 0 24px 80px rgba(0,0,0,0.22), 0 4px 16px rgba(0,0,0,0.1);
  text-align: center;
  animation: trd-modal-in 0.3s cubic-bezier(0.34,1.56,0.64,1) forwards;
  position: relative;
  overflow: hidden;
}
.trd-modal.closing {
  animation: trd-modal-out 0.18s ease forwards;
}
.trd-modal::before {
  content: '';
  position: absolute; top: 0; left: 0; right: 0;
  height: 4px;
}
.trd-modal.success::before { background: linear-gradient(90deg, #0e3725, #c9a84c, #0e3725); }
.trd-modal.error::before   { background: linear-gradient(90deg, #dc2626, #f87171, #dc2626); }
.trd-modal.confirm::before { background: linear-gradient(90deg, #0e3725, #c9a84c, #0e3725); }

.trd-icon-wrap {
  width: 80px; height: 80px;
  border-radius: 50%;
  margin: 0 auto 20px;
  display: flex; align-items: center; justify-content: center;
  position: relative;
  animation: trd-ring-in 0.35s cubic-bezier(0.34,1.56,0.64,1) 0.1s both;
}
.trd-icon-wrap.success { background: linear-gradient(135deg, #f0fdf4, #dcfce7); }
.trd-icon-wrap.error   { background: linear-gradient(135deg, #fef2f2, #fee2e2); }
.trd-icon-wrap.confirm { background: linear-gradient(135deg, #fffbeb, #fef3c7); }

.trd-ring-pulse {
  position: absolute; inset: -8px;
  border-radius: 50%;
  animation: trd-ring-pulse 1.2s ease-out 0.3s infinite;
}
.success .trd-ring-pulse { border: 2px solid rgba(14,55,37,0.25); }
.error   .trd-ring-pulse { border: 2px solid rgba(220,38,38,0.25); }
.confirm .trd-ring-pulse { border: 2px solid rgba(201,168,76,0.35); }

.trd-check {
  stroke-dasharray: 60;
  stroke-dashoffset: 60;
  animation: trd-icon-success 0.5s ease 0.25s forwards;
}
.trd-cross-line {
  stroke-dasharray: 40;
  stroke-dashoffset: 40;
  animation: trd-icon-error 0.35s ease 0.25s forwards;
}

.trd-title {
  font-family: 'Cairo', 'Ibarra Real Nova', Georgia, serif;
  font-size: 22px; font-weight: 700;
  margin: 0 0 8px;
  letter-spacing: -0.3px;
}
.trd-title.success { color: #0e3725; }
.trd-title.error   { color: #991b1b; }
.trd-title.confirm { color: #0e3725; }

.trd-msg {
  font-size: 14.5px; color: #4b5563;
  line-height: 1.6; margin: 0 0 28px;
  font-family: 'Cairo', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}
.trd-modal.rtl {
  direction: rtl;
  text-align: right;
}

.trd-btn-ok {
  padding: 12px 40px;
  border: none; border-radius: 10px;
  font-size: 14px; font-weight: 700;
  cursor: pointer; letter-spacing: 0.3px;
  transition: transform 0.15s, box-shadow 0.15s;
}
.trd-btn-ok:hover  { transform: translateY(-1px); }
.trd-btn-ok:active { transform: translateY(0); }
.trd-btn-ok.success, .trd-btn-ok.confirm-ok {
  background: linear-gradient(135deg, #0e3725, #1a5c3a);
  color: #c9a84c;
  box-shadow: 0 4px 16px rgba(14,55,37,0.35);
}
.trd-btn-ok.error {
  background: linear-gradient(135deg, #dc2626, #b91c1c);
  color: #fff;
  box-shadow: 0 4px 16px rgba(220,38,38,0.3);
}

.trd-btn-row { display: flex; gap: 10px; justify-content: center; }
.trd-btn-cancel {
  padding: 12px 28px;
  border: 1.5px solid #e5e7eb; border-radius: 10px;
  font-size: 14px; font-weight: 600;
  cursor: pointer; background: #fff; color: #374151;
  transition: border-color 0.15s, transform 0.15s;
}
.trd-btn-cancel:hover { border-color: #9ca3af; transform: translateY(-1px); }
.trd-btn-confirm {
  padding: 12px 28px;
  border: none; border-radius: 10px;
  font-size: 14px; font-weight: 700;
  cursor: pointer;
  background: linear-gradient(135deg, #c9a84c, #a07a20);
  color: #fff;
  box-shadow: 0 4px 14px rgba(201,168,76,0.4);
  transition: transform 0.15s, box-shadow 0.15s;
}
.trd-btn-confirm:hover { transform: translateY(-1px); box-shadow: 0 6px 20px rgba(201,168,76,0.5); }
`;

// ─── inject styles once ───────────────────────────────────────────────────────
if (typeof document !== "undefined" && !document.getElementById("trd-styles")) {
  const tag = document.createElement("style");
  tag.id = "trd-styles";
  tag.textContent = styles;
  document.head.appendChild(tag);
}

// ─── mount helper ─────────────────────────────────────────────────────────────
function mount(node) {
  const container = document.createElement("div");
  document.body.appendChild(container);
  // Use ReactDOM.render (CRA React 17 compatible)
  const ReactDOM = require("react-dom");
  ReactDOM.render(node, container);
  return () => {
    ReactDOM.unmountComponentAtNode(container);
    container.remove();
  };
}

// ─── Dialog component ─────────────────────────────────────────────────────────
function DialogModal({ type, title, message, onOk, onConfirm, onCancel }) {
  const backdropRef = useRef(null);
  const modalRef = useRef(null);
  // Admin surfaces are permanently English/LTR regardless of app locale;
  // the dialog portals to <body>, outside the admin shell's dir wrapper.
  const isAdminSurface = typeof window !== "undefined" && window.location.pathname.startsWith("/admin");
  const isAr = i18n.language === "ar" && !isAdminSurface;

  const close = (cb) => {
    if (backdropRef.current) backdropRef.current.classList.add("closing");
    if (modalRef.current) modalRef.current.classList.add("closing");
    setTimeout(() => { cb && cb(); }, 180);
  };

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") close(onOk || onCancel); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const isSuccess = type === "success";
  const isError   = type === "error";
  const isConfirm = type === "confirm";

  return (
    <div className="trd-backdrop" ref={backdropRef}>
      <div className={`trd-modal ${type}${isAr ? " rtl" : ""}`} ref={modalRef}>
        {/* Icon */}
        <div className={`trd-icon-wrap ${type}`}>
          <div className="trd-ring-pulse" />
          {isSuccess && (
            <svg width="38" height="38" viewBox="0 0 38 38" fill="none">
              <circle cx="19" cy="19" r="17" stroke="#0e3725" strokeWidth="2" opacity="0.15"/>
              <polyline className="trd-check" points="10,20 16,27 28,13"
                stroke="#0e3725" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
            </svg>
          )}
          {isError && (
            <svg width="38" height="38" viewBox="0 0 38 38" fill="none">
              <circle cx="19" cy="19" r="17" stroke="#dc2626" strokeWidth="2" opacity="0.2"/>
              <line className="trd-cross-line" x1="13" y1="13" x2="25" y2="25"
                stroke="#dc2626" strokeWidth="3" strokeLinecap="round"/>
              <line className="trd-cross-line" x1="25" y1="13" x2="13" y2="25"
                stroke="#dc2626" strokeWidth="3" strokeLinecap="round"/>
            </svg>
          )}
          {isConfirm && (
            <svg width="38" height="38" viewBox="0 0 38 38" fill="none">
              <circle cx="19" cy="19" r="17" stroke="#c9a84c" strokeWidth="2" opacity="0.3"/>
              <path className="trd-check" d="M19 12v8M19 24v2"
                stroke="#a07a20" strokeWidth="3" strokeLinecap="round"/>
            </svg>
          )}
        </div>

        {/* Text */}
        <div className={`trd-title ${type}`}>{title}</div>
        <div className="trd-msg">{message}</div>

        {/* Buttons */}
        {!isConfirm ? (
          <button
            className={`trd-btn-ok ${type}`}
            onClick={() => close(onOk)}
          >
            {isAr ? "حسناً" : "OK"}
          </button>
        ) : (
          <div className="trd-btn-row">
            <button className="trd-btn-cancel" onClick={() => close(onCancel)}>{isAr ? "إلغاء" : "Cancel"}</button>
            <button className="trd-btn-confirm" onClick={() => close(onConfirm)}>{isAr ? "تأكيد" : "Confirm"}</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Public API ───────────────────────────────────────────────────────────────
export const dialog = {
  // Generic notice: error styling when the title says so, success otherwise.
  alert(title, message) {
    return /error|fail/i.test(String(title)) ? dialog.error(title, message) : dialog.success(title, message);
  },
  success(title, message) {
    return new Promise((resolve) => {
      const unmount = mount(
        <DialogModal type="success" title={title} message={message}
          onOk={() => { unmount(); resolve(); }} />
      );
    });
  },
  error(title, message) {
    return new Promise((resolve) => {
      const unmount = mount(
        <DialogModal type="error" title={title} message={message}
          onOk={() => { unmount(); resolve(); }} />
      );
    });
  },
  confirm(title, message) {
    return new Promise((resolve) => {
      const unmount = mount(
        <DialogModal type="confirm" title={title} message={message}
          onConfirm={() => { unmount(); resolve(true); }}
          onCancel={() => { unmount(); resolve(false); }} />
      );
    });
  },
};

export default DialogModal;
